import { Platform } from 'react-native';
import { Video } from 'react-native-compressor';
import * as ImagePicker from 'expo-image-picker';
import { ReactNativeFile } from 'apollo-upload-client';

import { compress, getSmallBlurhash } from './image';
import { FileNotSupported } from '../errors';

const BLURHASH_RESOLUTION: [number, number] = [1, 1];

export async function processSelectedMedia({
	type,
	uri,
	base64 = '',
	file,
}: ImagePicker.ImagePickerAsset & { file?: File }) {
	const SUPPORTED_FILE_TYPES = ['image', 'video'];
	const fileType = type || getTypeFromBase64(uri);
	if (!SUPPORTED_FILE_TYPES.includes(fileType)) {
		throw new FileNotSupported();
	}

	if (fileType === 'video') {
		const compressedCorrectUri = await compressVideo(uri);

		return {
			type: 'video' as const,
			uri: compressedCorrectUri,
			base64: base64 || '',
			file,
		};
	}

	const [compressed, blurhash] = await Promise.all([
		compress(uri),
		getSmallBlurhash(uri, BLURHASH_RESOLUTION),
	]);

	return {
		type: 'image' as const,
		uri: compressed.uri,
		base64: compressed.base64 || '',
		blurhash,
		file,
	};
}

export async function compressVideo(uri: string) {
	const compressedVideoUri =
		Platform.OS === 'web'
			? uri
			: await Video.compress(uri, {
					compressionMethod: 'auto',
				});

	/*
	 On android path need to start with file:///, library returns file://
	 works on iOS too
	*/
	const compressedCorrectUri = compressedVideoUri.replace('//', '///');

	return compressedCorrectUri;
}

export function getTypeFromBase64(base64String: string) {
	const [header] = base64String.split(';');
	const [, typeString] = header.split(':');
	const [type] = typeString.split('/');

	return type;
}

export async function prepareMediaFile(media: SelectedMedia) {
	if (Platform.OS !== 'web') {
		const type = getType(media);

		return new ReactNativeFile({
			name: 'uploaded-media',
			uri: media.uri,
			type,
		});
	}

	if (media.file) {
		return media.file;
	}

	return getImageBlobFromDataUri(media.uri);
}

function getType(file: SelectedMedia) {
	// https://github.com/jaydenseric/apollo-upload-client/issues/109#issuecomment-562271192
	if (Platform.OS === 'android') {
		return `${file.type || 'image'}/*`;
	}

	return file.type;
}

async function getImageBlobFromDataUri(dataUri: string) {
	const response = await fetch(dataUri);

	return response.blob();
}

export type SelectedMedia = {
	id?: string;
	uri: string;
	base64: string;
	type?: 'image' | 'video';
	blurhash?: string;
	file?: File;
};
